import { ChangeDetectionStrategy, Component, EnvironmentInjector, OnInit, ViewChild, ViewContainerRef, ElementRef, Renderer2, OnDestroy, Inject, DOCUMENT } from '@angular/core';

import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { DeviceType } from 'src/app/shared/enums/device-type.enum';
import { PageType } from '../shared/enums/page-type.enum';
import { BreadcrumbService } from '../shared/services/breadcrumb.service';
import { SidePanelOptions, SidepanelService } from '../shared/services/sidepanel.service';
import { Global } from '../shared/types/global.type';
import { LoadingService } from '../shared/services/loading.service';
import { AuthService } from '../shared/services/auth.service';
import { ClientPermission } from '../shared/enums/generated.enums';
import { ChatComponent } from './chat/chat.component';
import { StorageService } from '../shared/services/storage.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {
  @ViewChild(MatSidenav, { static: true }) private sidePanel: MatSidenav;
  @ViewChild(MatSidenav, { static: true, read: ElementRef }) private sidePanelElement: ElementRef;
  @ViewChild(MatSidenavContent, { static: true, read: ElementRef }) private sidePanelContentElement: ElementRef;
  @ViewChild('sidepanelContent', { static: true, read: ViewContainerRef }) private vcf: ViewContainerRef;

  themeLayout = 'vertical';
  layoutType = 'light';
  profileMenu = 'an-off';
  profileMenuClass: string;
  navMenuLeft = 'an-on';

  resizingEvent = {
    isResizing: false,
    startingCursorX: 0,
    startingWidth: 0,
  };

  private mouseMoveListener: (() => void) | null = null;
  private mouseUpListener: (() => void) | null = null;

  private windowWidth: number;
  readonly PageType = PageType;
  readonly DeviceType = DeviceType;

  get showBreadcrumb(): boolean {
    return this.breadcrumbService.breadcrumbItems?.length > 0;
  }

  get deviceType(): DeviceType {
    return Global.deviceType;
  }

  get sidenavSettings(): SidePanelOptions {
    return this.sidepanelService.settings;
  }

  async sidenavClose(): Promise<void> {
    await this.sidepanelService.close();
  }

  get isSidenavOpen(): boolean {
    return this.sidepanelService.isOpened;
  }

  loading$ = this.loadingService.loading$;

  constructor(
    private breadcrumbService: BreadcrumbService,
    private sidepanelService: SidepanelService,
    private loadingService: LoadingService,
    private authService: AuthService,
    private environmentInjector: EnvironmentInjector,
    private renderer: Renderer2,
    private storageService: StorageService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.windowWidth = window.innerWidth;
    this.setMenuAttributes(this.windowWidth);
    this.setHeaderAttributes(this.windowWidth);
  }

  ngOnInit(): void {
    this.sidepanelService.setPanel(this.sidePanel, this.sidePanelElement);
    this.sidepanelService.setContentVcf(this.vcf);
  }

  ngOnDestroy(): void {
    this.cleanupEventListeners();
  }

  onResize(event: UIEvent): void {
    const target = event.target as Window;
    this.windowWidth = target.innerWidth;
    this.setHeaderAttributes(this.windowWidth);

    let reSizeFlag = true;
    if (Global.deviceType === DeviceType.tablet && this.windowWidth >= Global.mobileBreakpoint && this.windowWidth <= 992) {
      reSizeFlag = false;
    } else if (Global.deviceType === DeviceType.phone && this.windowWidth < Global.mobileBreakpoint) {
      reSizeFlag = false;
    }
    /* for check device */
    if (reSizeFlag) {
      this.setMenuAttributes(this.windowWidth);
    }
  }

  async openChatPanel(): Promise<void> {
    const mode = this.getSidenavMode();
    await this.sidepanelService.open(ChatComponent, {
      hasBackdrop: mode === 'over', // Enable backdrop for overlay mode
      size: 'medium',
      autosize: false,
      cancelOnRouteChange: false,
      mode: mode,
      resizable: true,
    }, d => {
    }, this.environmentInjector);
  }

  /**
   * Determines the appropriate sidenav mode based on device type and screen size
   */
  private getSidenavMode(): 'over' | 'push' | 'side' {
    switch (this.deviceType) {
      case DeviceType.phone:
        return 'over'; // Always overlay on mobile
      case DeviceType.tablet:
        return 'over'; // Overlay on tablet to save space
      case DeviceType.desktop:
        return this.windowWidth >= 1400 ? 'side' : 'push'; // Side-by-side on large screens, push on smaller desktop
      default:
        return 'over';
    }
  }

  startResizing(event: MouseEvent): void {
    this.resizingEvent = {
      isResizing: true,
      startingCursorX: event.clientX,
      startingWidth: this.sidepanelService.panelWidth,
    };
    // Add visual feedback during resize
    this.renderer.setStyle(this.document.body, 'cursor', 'col-resize');

    // Prevent text selection during resize
    event.preventDefault();

    // Setup mouse event listeners
    this.setupResizeListeners();
  }

  private updateSidenavWidth(event: MouseEvent): void {
    if (!this.resizingEvent.isResizing) {
      return;
    }
    const cursorDeltaX = event.clientX - this.resizingEvent.startingCursorX;
    const newWidth = this.resizingEvent.startingWidth - cursorDeltaX;

    const minWidth = Global.minSidenavWidth;
    const maxWidth = window.innerWidth * Global.maxSidenavWidthFactor;
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    // Apply the new width to both sidenav and content elements
    if (this.sidePanel?.opened) {
      const sidenavElement = this.sidePanelElement?.nativeElement as HTMLElement | undefined;
      const contentElement = this.sidePanelContentElement?.nativeElement as HTMLElement | undefined;

      if (sidenavElement) {
        this.renderer.setStyle(sidenavElement, 'width', `${constrainedWidth}px`);
        this.renderer.setStyle(sidenavElement, 'minWidth', `${constrainedWidth}px`);
      }

      // Update content area when in 'side' mode
      if (contentElement && this.sidepanelService.getCurrentMode() === 'side') {
        const availableWidth = window.innerWidth - constrainedWidth;
        this.renderer.setStyle(contentElement, 'width', `${availableWidth}px`);
        this.renderer.setStyle(contentElement, 'marginRight', `${constrainedWidth}px`);
      }
    }
  }

  private stopResizing(): void {
    if (this.resizingEvent.isResizing) {
      this.resizingEvent.isResizing = false;

      // Restore cursor
      this.renderer.removeStyle(this.document.body, 'cursor');

      // Update the service's internal width tracking if possible
      const sidenavElement = this.sidePanelElement?.nativeElement as HTMLElement | undefined;
      if (sidenavElement) {
        const finalWidth = parseInt(window.getComputedStyle(sidenavElement).width, 10);
        // Store the width for future reference
        if (!isNaN(finalWidth)) {
          this.storageService.sidenavWidth = finalWidth.toString();
        }
      }

      // Clean up event listeners
      this.cleanupEventListeners();
    }
  }

  private setupResizeListeners(): void {
    // Clean up any existing listeners first
    this.cleanupEventListeners();

    this.mouseMoveListener = this.renderer.listen('window', 'mousemove', (event: MouseEvent) => {
      this.updateSidenavWidth(event);
    });

    this.mouseUpListener = this.renderer.listen('window', 'mouseup', () => {
      this.stopResizing();
    });
  }

  private cleanupEventListeners(): void {
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
      this.mouseMoveListener = null;
    }
    if (this.mouseUpListener) {
      this.mouseUpListener();
      this.mouseUpListener = null;
    }
  }

  get aiAgentEnabled(): boolean {
    return this.authService.hasPermission(ClientPermission.AiAgent);
  }

  private setHeaderAttributes(windowWidth: number): void {
    if (windowWidth <= Global.mobileBreakpoint) {
      this.navMenuLeft = 'an-off';
    } else {
      this.navMenuLeft = 'an-on';
    }
  }

  private setMenuAttributes(windowWidth: number): void {
    // for check device type and set the device type in global variable mobile(768), tablet(992), desktop(1366)
    if (windowWidth < Global.mobileBreakpoint) {
      Global.deviceType = DeviceType.phone;
    } else if (windowWidth >= Global.mobileBreakpoint && windowWidth < Global.tabletBreakpoint) {
      Global.deviceType = DeviceType.tablet;
    } else if (windowWidth >= Global.tabletBreakpoint && windowWidth < Global.desktopBreakpoint) {
      Global.deviceType = DeviceType.desktop;
    } else {
      Global.deviceType = DeviceType.desktop;
    }
  }
}
